import Rollbar from "rollbar"
import { type Ref, ref } from "vue"
import { RouteLocationNormalized as Route } from "vue-router"
import { apiWebNurseryAutoShiftStatesPath } from "~/rails/routes"

type AutoShiftState = {
  processing: boolean
  timeoutId: number | null
}

type StateData = {
  processing: boolean
  timeout?: boolean
}

const autoShiftStateMap = new Map<string, Ref<any>>()

const getOrCreateState = <T>(key: string, init: () => T): Ref<T> => {
  if (typeof useState === "function") {
    return useState<T>(key, init)
  }
  if (!autoShiftStateMap.has(key)) {
    autoShiftStateMap.set(key, ref(init()))
  }
  return autoShiftStateMap.get(key)!
}

export const useAutoShift = () => {
  const state = getOrCreateState<AutoShiftState>("auto_shift_state", () => ({
    processing: false,
    timeoutId: null,
  }))

  const setProcessing = (processing: boolean) => {
    state.value.processing = processing
  }

  const setTid = (timeoutId: number | null) => {
    state.value.timeoutId = timeoutId
  }

  const startPolling = (payload: {
    axios: any
    rollbar?: Rollbar
    timeout?: number
    reloadAfterPolling?: boolean
    route?: Route
    redirect?: (path: string) => void
  }) => {
    if (state.value.timeoutId) {
      return
    }

    const timeoutId =
      typeof window !== "undefined"
        ? window.setTimeout(() => runCheckProcessing(payload), payload.timeout || 3000)
        : null
    setTid(timeoutId)
  }

  const runCheckProcessing = async (payload: {
    axios: any
    rollbar?: Rollbar
    route?: Route
    redirect?: (path: string) => void
  }) => {
    try {
      setTid(null)
      const data = (await (payload.axios as any).$get(
        apiWebNurseryAutoShiftStatesPath(),
      )) as StateData
      if (data.processing) {
        if (payload.route && payload.route.path !== "/" && payload.redirect) {
          payload.redirect("/")
        }
        setProcessing(true)
        const timeout = data.timeout ? 1800000 : 3000
        startPolling({ ...payload, timeout })
      } else if (state.value.processing) {
        setProcessing(false)
        console.warn("[useAutoShift] Reload disabled for debugging.")
        // window.location.reload()
      }
    } catch (e) {
      console.error(e)
      payload.rollbar?.error(e as Error)
    }
  }

  return {
    state,
    setProcessing,
    setTid,
    startPolling,
    runCheckProcessing,
  }
}
